import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../lib/auth';
import { useNetwork, confidenceFor as readConfidence, planKeyFor } from '../lib/networkStore';
import { Phone, CheckCircle2, AlertCircle, ShieldCheck } from 'lucide-react';
import { toast } from '../lib/aiToast';

const today = () => new Date().toISOString().slice(0, 10);

// Phone-call capture form (gap 2.4). Records a payer verification call and adds
// the plan to the network library with full provenance (gap 2.5). Every field is
// part of the audit trail — the evidence a later appeal is built from.
export default function NetworkCallForm({ open, onClose, item }) {
  const { session } = useAuth();
  const { logVerification, addPlan } = useNetwork();
  const [staffName, setStaffName] = useState('');
  const [repName, setRepName] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [callDate, setCallDate] = useState(today);
  const [result, setResult] = useState('in_network');

  // Reset to defaults whenever a new item opens the form.
  useEffect(() => {
    if (open) {
      setStaffName(session?.user?.name || '');
      setRepName('');
      setReferenceNumber('');
      setCallDate(today());
      setResult('in_network');
    }
  }, [open, item, session]);

  if (!item) return null;

  const canSubmit = staffName.trim() && repName.trim() && referenceNumber.trim();

  const submit = () => {
    if (!canSubmit) return;
    logVerification({
      practiceId: item.practiceId,
      providerId: item.providerId,
      payerId: item.payerId,
      planName: item.planNameReturned,
      patientId: item.patientId,
      result,
      source: 'phone_call',
      referenceNumber: referenceNumber.trim(),
      repName: repName.trim(),
      staffName: staffName.trim(),
      date: callDate,
    });
    addPlan({
      practiceId: item.practiceId,
      providerId: item.providerId,
      payerId: item.payerId,
      planName: item.planNameReturned,
      status: result,
      source: 'phone_call',
      addedBy: staffName.trim(),
      evidence: { referenceNumber: referenceNumber.trim(), repName: repName.trim(), callDate },
    });

    // Reflect where this leaves the plan: high confidence stops the routing.
    // Read fresh (post-write) confidence — the mutators persist synchronously.
    const c = readConfidence({
      planKey: planKeyFor(item.payerId, item.planNameReturned),
      practiceId: item.practiceId,
      providerId: item.providerId,
    });
    toast.success(
      c.level === 'high'
        ? `Verified — ${item.planNameReturned} added at high confidence (${c.count}/${c.threshold}). Cleared from the calling queue.`
        : `Verification recorded — ${item.planNameReturned} added to the network library (${c.count}/${c.threshold} confirmations).`
    );
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Phone className="h-4 w-4 text-brand-600" />Verify network by phone</DialogTitle>
          <DialogDescription>
            {item.payerName} · {item.planNameReturned}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5 py-1">
          <div className="grid gap-1.5">
            <Label htmlFor="nf-staff">Staff member on the call</Label>
            <Input id="nf-staff" value={staffName} onChange={(e) => setStaffName(e.target.value)} placeholder="Your name" />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="nf-rep">Payer representative</Label>
            <Input id="nf-rep" value={repName} onChange={(e) => setRepName(e.target.value)} placeholder="Name the rep gave you" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="nf-ref">Call reference #</Label>
              <Input id="nf-ref" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} placeholder="e.g. CIG-4471902" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="nf-date">Call date</Label>
              <Input id="nf-date" type="date" value={callDate} onChange={(e) => setCallDate(e.target.value)} />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Confirmed status</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setResult('in_network')}
                className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${result === 'in_network' ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              >
                <CheckCircle2 className="h-4 w-4" />In-network
              </button>
              <button
                type="button"
                onClick={() => setResult('out_of_network')}
                className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${result === 'out_of_network' ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              >
                <AlertCircle className="h-4 w-4" />Out-of-network
              </button>
            </div>
          </div>
          <p className="flex items-start gap-1.5 text-[11px] text-slate-400">
            <ShieldCheck className="h-3.5 w-3.5 mt-px shrink-0" />
            Saved to the network library with source, staff, rep, reference number and date — a traceable audit trail for appeals.
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose?.()}>Cancel</Button>
          <Button className="bg-brand-600 hover:bg-brand-700 text-white" disabled={!canSubmit} onClick={submit}>
            Record verification
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
